import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { PERMISSION_AREAS } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Copy, Lock, Save, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import RolePermissionMatrix from "./RolePermissionMatrix";

interface StaffRole {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  is_locked: boolean;
  color: string;
  permissions: Record<string, string | boolean>;
  sort_order: number;
  is_view_only: boolean;
  created_at: string;
}

const DEFAULT_PERMISSIONS = Object.fromEntries(
  PERMISSION_AREAS.map((a) => [a, "none"])
) as Record<string, string>;

const SPECIAL_KEYS = [
  "can_refund_payments",
  "can_cancel_bookings",
  "can_export_data",
  "can_view_revenue",
];

function splitPermissions(perms: Record<string, string | boolean>) {
  const areaPerms: Record<string, string> = {};
  const specialPerms: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(perms)) {
    if (SPECIAL_KEYS.includes(k)) {
      specialPerms[k] = v === true;
    } else {
      areaPerms[k] = v as string;
    }
  }
  return { areaPerms, specialPerms };
}

function mergePermissions(
  area: Record<string, string>,
  special: Record<string, boolean>
): Record<string, string | boolean> {
  return { ...area, ...special };
}

export default function SettingsRoles() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const orgId = organization?.id;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editColor, setEditColor] = useState("#6366f1");
  const [editAreaPerms, setEditAreaPerms] = useState<Record<string, string>>(DEFAULT_PERMISSIONS);
  const [editSpecialPerms, setEditSpecialPerms] = useState<Record<string, boolean>>({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Fetch roles
  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ["staff-roles", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff_roles" as any)
        .select("*")
        .eq("organization_id", orgId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as StaffRole[];
    },
    enabled: !!orgId,
  });

  // Fetch assignment counts per role
  const { data: assignmentCounts = {} } = useQuery({
    queryKey: ["staff-assignment-counts", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff_assignments" as any)
        .select("role_id")
        .eq("organization_id", orgId!);
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const row of (data ?? []) as any[]) {
        counts[row.role_id] = (counts[row.role_id] || 0) + 1;
      }
      return counts;
    },
    enabled: !!orgId,
  });

  const selectedRole = roles.find((r) => r.id === selectedId) ?? null;

  function selectRole(role: StaffRole) {
    setSelectedId(role.id);
    setEditName(role.name);
    setEditDescription(role.description ?? "");
    setEditColor(role.color);
    const { areaPerms, specialPerms } = splitPermissions(role.permissions ?? {});
    setEditAreaPerms({ ...DEFAULT_PERMISSIONS, ...areaPerms });
    setEditSpecialPerms(specialPerms);
  }

  // Create role
  const createMutation = useMutation({
    mutationFn: async (overrides?: { name: string; permissions: Record<string, string | boolean> }) => {
      const maxSort = roles.reduce((max, r) => Math.max(max, r.sort_order ?? 0), 0);
      const { data, error } = await supabase
        .from("staff_roles" as any)
        .insert({
          organization_id: orgId,
          name: overrides?.name ?? t("rolesPage.newRoleName", "Ny roll"),
          description: "",
          color: "#6366f1",
          permissions: overrides?.permissions ?? DEFAULT_PERMISSIONS,
          sort_order: maxSort + 1,
          is_system: false,
          is_locked: false,
          is_view_only: false,
        } as any)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as StaffRole;
    },
    onSuccess: (newRole) => {
      queryClient.invalidateQueries({ queryKey: ["staff-roles", orgId] });
      selectRole(newRole);
      toast.success(t("rolesPage.roleCreated", "Roll skapad"));
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Update role
  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("No role selected");
      const permissions = mergePermissions(editAreaPerms, editSpecialPerms);
      const { error } = await supabase
        .from("staff_roles" as any)
        .update({
          name: editName,
          description: editDescription,
          color: editColor,
          permissions,
        } as any)
        .eq("id", selectedId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-roles", orgId] });
      toast.success(t("rolesPage.roleSaved", "Roll sparad"));
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Delete role
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("No role selected");
      const { error } = await supabase
        .from("staff_roles" as any)
        .delete()
        .eq("id", selectedId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-roles", orgId] });
      setSelectedId(null);
      setConfirmDeleteOpen(false);
      toast.success(t("rolesPage.roleDeleted", "Roll borttagen"));
    },
    onError: (err: any) => {
      setConfirmDeleteOpen(false);
      toast.error(err.message);
    },
  });

  function handleDelete() {
    if (!selectedRole) return;
    const count = assignmentCounts[selectedRole.id] ?? 0;
    if (count > 0) {
      toast.error(
        t("rolesPage.cannotDeleteAssigned", "Kan inte ta bort en roll som har tilldelade användare")
      );
      return;
    }
    setConfirmDeleteOpen(true);
  }

  function handleCopy() {
    if (!selectedRole) return;
    createMutation.mutate({
      name: `Kopia av ${selectedRole.name}`,
      permissions: selectedRole.permissions,
    });
  }

  if (rolesLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-6">
      {/* Left panel: role list */}
      <div className="w-full md:w-1/3 space-y-3">
        <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
          {roles.map((role) => {
            const count = assignmentCounts[role.id] ?? 0;
            return (
              <button
                key={role.id}
                onClick={() => selectRole(role)}
                className={cn(
                  "w-full text-left rounded-lg border p-3 transition-colors",
                  selectedId === role.id
                    ? "border-primary bg-accent"
                    : "border-border hover:bg-muted/50"
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-full shrink-0"
                    style={{ backgroundColor: role.color }}
                  />
                  <span className="font-medium truncate flex-1">{role.name}</span>
                  {role.is_locked && <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                  <Badge variant="secondary" className="text-xs shrink-0">
                    {count}
                  </Badge>
                </div>
              </button>
            );
          })}
        </div>

        <Separator />

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-1" />
            {t("rolesPage.newRole", "Ny roll")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={!selectedRole || createMutation.isPending}
          >
            <Copy className="h-4 w-4 mr-1" />
            {t("rolesPage.copyRole", "Kopiera roll")}
          </Button>
        </div>
      </div>

      {/* Right panel: edit form */}
      <div className="w-full md:w-2/3">
        {!selectedRole ? (
          <div className="text-muted-foreground text-sm py-12 text-center">
            {t("rolesPage.selectRoleHint", "Välj en roll till vänster för att redigera")}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("rolesPage.roleName", "Namn")}</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  disabled={selectedRole.is_locked}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("rolesPage.roleColor", "Färg")}</Label>
                <Input
                  type="color"
                  value={editColor}
                  onChange={(e) => setEditColor(e.target.value)}
                  className="h-10 w-20 p-1 cursor-pointer"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t("rolesPage.roleDescription", "Beskrivning")}</Label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={2}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <Separator />

            <RolePermissionMatrix
              permissions={editAreaPerms}
              specialPermissions={editSpecialPerms}
              onChange={(area, level) =>
                setEditAreaPerms((prev) => ({ ...prev, [area]: level }))
              }
              onSpecialChange={(key, value) =>
                setEditSpecialPerms((prev) => ({ ...prev, [key]: value }))
              }
              disabled={selectedRole.is_locked}
            />

            <Separator />

            <div className="flex gap-2">
              <Button
                onClick={() => updateMutation.mutate()}
                disabled={selectedRole.is_locked || updateMutation.isPending}
              >
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                {t("rolesPage.save", "Spara")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={selectedRole.is_locked || deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {t("rolesPage.delete", "Ta bort")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Confirm delete dialog */}
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("rolesPage.confirmDeleteTitle", "Ta bort roll")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t(
              "rolesPage.confirmDeleteMessage",
              "Är du säker på att du vill ta bort denna roll? Åtgärden kan inte ångras."
            )}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)}>
              {t("rolesPage.cancel", "Avbryt")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              {t("rolesPage.confirmDelete", "Ta bort")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
